// src/redux/subscriptionSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import billingService from "../services/billingService";

const safeStr = (value) =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const unwrapPayload = (value) => {
  if (!value || typeof value !== "object") return value;

  if (value.data && typeof value.data === "object") return value.data;
  if (value.payload && typeof value.payload === "object") return value.payload;
  if (value.response?.data && typeof value.response.data === "object") {
    return value.response.data;
  }

  return value;
};

const readNestedError = (value) => {
  const payload = unwrapPayload(value);

  if (!payload || typeof payload !== "object") return payload;

  if (payload.error && typeof payload.error === "object") {
    return payload.error;
  }

  if (payload.payload?.error && typeof payload.payload.error === "object") {
    return payload.payload.error;
  }

  return payload;
};

const safeMsg = (error, fallback) => {
  const payload = unwrapPayload(error);
  const nested = readNestedError(error);

  const raw =
    nested?.message ||
    nested?.detail ||
    nested?.error_description ||
    payload?.message ||
    payload?.detail ||
    payload?.error_description ||
    error?.message ||
    payload?.error ||
    "";

  return safeStr(raw).trim() || fallback;
};

const getPlanKey = (plan) => {
  const value =
    plan?.plan_id ??
    plan?.planId ??
    plan?.price_id ??
    plan?.priceId ??
    plan?.id ??
    null;

  const key = safeStr(value).trim();

  return key || null;
};

const normalizePlansPayload = (payload) => {
  const data = unwrapPayload(payload);

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.plans)) return data.plans;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

const readCheckoutUrl = (payload) => {
  const data = unwrapPayload(payload);

  if (typeof data === "string") return data;

  return safeStr(
    data?.checkout_url ||
      data?.checkoutUrl ||
      data?.url ||
      data?.session_url ||
      data?.sessionUrl ||
      data?.session?.url ||
      data?.checkout?.url ||
      ""
  ).trim();
};

const readCheckoutSessionId = (payload) => {
  const data = unwrapPayload(payload);

  return safeStr(
    data?.session_id ||
      data?.sessionId ||
      data?.id ||
      data?.session?.id ||
      data?.checkout?.session_id ||
      data?.checkout?.sessionId ||
      ""
  ).trim();
};

const readPortalUrl = (payload) => {
  const data = unwrapPayload(payload);

  if (typeof data === "string") return data;

  return safeStr(
    data?.portal_url ||
      data?.portalUrl ||
      data?.url ||
      data?.session_url ||
      data?.sessionUrl ||
      data?.session?.url ||
      data?.portal?.url ||
      ""
  ).trim();
};

export const fetchBillingPlansAsync = createAsyncThunk(
  "subscription/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      return await billingService.getPlans();
    } catch (error) {
      return rejectWithValue(safeMsg(error, "Failed to fetch plans"));
    }
  }
);

export const fetchBillingSubscriptionAsync = createAsyncThunk(
  "subscription/fetchSubscription",
  async (_, { rejectWithValue }) => {
    try {
      return await billingService.getSubscription();
    } catch (error) {
      return rejectWithValue(safeMsg(error, "Failed to fetch subscription"));
    }
  }
);

export const createCheckoutSessionAsync = createAsyncThunk(
  "subscription/createCheckoutSession",
  async (payload, { rejectWithValue }) => {
    try {
      return await billingService.createCheckoutSession(payload);
    } catch (error) {
      return rejectWithValue(
        safeMsg(error, "Failed to create checkout session")
      );
    }
  }
);

export const createPortalSessionAsync = createAsyncThunk(
  "subscription/createPortalSession",
  async (payload, { rejectWithValue }) => {
    try {
      return await billingService.createPortalSession(payload || {});
    } catch (error) {
      return rejectWithValue(safeMsg(error, "Failed to open billing portal"));
    }
  }
);

const initialState = {
  plans: [],
  plansLoading: false,
  plansError: "",

  subscription: null,
  subscriptionLoading: false,
  subscriptionError: "",

  selectedPlan: null,

  checkoutLoading: false,
  checkoutError: "",
  checkoutUrl: "",
  checkoutSessionId: "",
  checkoutRequestId: null,

  portalLoading: false,
  portalError: "",
  portalUrl: "",
  portalRequestId: null,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    selectPlan(state, action) {
      state.selectedPlan = action.payload || null;

      state.checkoutError = "";
      state.checkoutUrl = "";
      state.checkoutSessionId = "";
      state.checkoutRequestId = null;
    },

    clearErrors(state) {
      state.plansError = "";
      state.checkoutError = "";
      state.subscriptionError = "";
      state.portalError = "";
    },

    resetCheckout(state) {
      state.checkoutLoading = false;
      state.checkoutError = "";
      state.checkoutUrl = "";
      state.checkoutSessionId = "";
      state.checkoutRequestId = null;
    },

    resetPortal(state) {
      state.portalLoading = false;
      state.portalError = "";
      state.portalUrl = "";
      state.portalRequestId = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingPlansAsync.pending, (state) => {
        state.plansLoading = true;
        state.plansError = "";
      })
      .addCase(fetchBillingPlansAsync.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plansError = "";
        state.plans = normalizePlansPayload(action.payload);

        if (state.selectedPlan) {
          const selectedKey = getPlanKey(state.selectedPlan);

          if (selectedKey) {
            const stillExists = state.plans.some(
              (plan) => String(getPlanKey(plan) || "") === String(selectedKey)
            );

            if (!stillExists) {
              state.selectedPlan = null;
              state.checkoutUrl = "";
              state.checkoutSessionId = "";
              state.checkoutError = "";
              state.checkoutRequestId = null;
            }
          }
        }
      })
      .addCase(fetchBillingPlansAsync.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = safeStr(action.payload) || "Failed to fetch plans";
      })

      .addCase(fetchBillingSubscriptionAsync.pending, (state) => {
        state.subscriptionLoading = true;
        state.subscriptionError = "";
      })
      .addCase(fetchBillingSubscriptionAsync.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = "";
        state.subscription = unwrapPayload(action.payload) || null;
      })
      .addCase(fetchBillingSubscriptionAsync.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError =
          safeStr(action.payload) || "Failed to fetch subscription";
      })

      .addCase(createCheckoutSessionAsync.pending, (state, action) => {
        state.checkoutLoading = true;
        state.checkoutError = "";
        state.checkoutUrl = "";
        state.checkoutSessionId = "";
        state.checkoutRequestId = action.meta.requestId;
      })
      .addCase(createCheckoutSessionAsync.fulfilled, (state, action) => {
        if (state.checkoutRequestId !== action.meta.requestId) return;

        state.checkoutLoading = false;
        state.checkoutRequestId = null;
        state.checkoutError = "";
        state.checkoutUrl = readCheckoutUrl(action.payload);
        state.checkoutSessionId = readCheckoutSessionId(action.payload);

        if (!state.checkoutUrl) {
          state.checkoutError =
            "Checkout session was created, but no checkout URL was returned.";
        }
      })
      .addCase(createCheckoutSessionAsync.rejected, (state, action) => {
        if (state.checkoutRequestId !== action.meta.requestId) return;

        state.checkoutLoading = false;
        state.checkoutRequestId = null;
        state.checkoutError =
          safeStr(action.payload) || "Failed to start checkout";
      })

      .addCase(createPortalSessionAsync.pending, (state, action) => {
        state.portalLoading = true;
        state.portalError = "";
        state.portalUrl = "";
        state.portalRequestId = action.meta.requestId;
      })
      .addCase(createPortalSessionAsync.fulfilled, (state, action) => {
        if (state.portalRequestId !== action.meta.requestId) return;

        state.portalLoading = false;
        state.portalRequestId = null;
        state.portalError = "";
        state.portalUrl = readPortalUrl(action.payload);

        if (!state.portalUrl) {
          state.portalError =
            "Billing portal session was created, but no portal URL was returned.";
        }
      })
      .addCase(createPortalSessionAsync.rejected, (state, action) => {
        if (state.portalRequestId !== action.meta.requestId) return;

        state.portalLoading = false;
        state.portalRequestId = null;
        state.portalError =
          safeStr(action.payload) || "Failed to open billing portal";
      });
  },
});

export const { selectPlan, clearErrors, resetCheckout, resetPortal } =
  subscriptionSlice.actions;

export const selectPlans = (state) => state.subscription?.plans || [];
export const selectPlansLoading = (state) =>
  state.subscription?.plansLoading || false;
export const selectPlansError = (state) => state.subscription?.plansError || "";

export const selectSelectedPlan = (state) =>
  state.subscription?.selectedPlan || null;

export const selectSubscription = (state) =>
  state.subscription?.subscription || null;
export const selectSubscriptionLoading = (state) =>
  state.subscription?.subscriptionLoading || false;
export const selectSubscriptionError = (state) =>
  state.subscription?.subscriptionError || "";

export const selectCheckoutLoading = (state) =>
  state.subscription?.checkoutLoading || false;
export const selectCheckoutError = (state) =>
  state.subscription?.checkoutError || "";
export const selectCheckoutUrl = (state) =>
  state.subscription?.checkoutUrl || "";
export const selectCheckoutSessionId = (state) =>
  state.subscription?.checkoutSessionId || "";

export const selectPortalLoading = (state) =>
  state.subscription?.portalLoading || false;
export const selectPortalError = (state) => state.subscription?.portalError || "";
export const selectPortalUrl = (state) => state.subscription?.portalUrl || "";

export default subscriptionSlice.reducer;